/**
 * Central company brand palette — single source of truth.
 *
 * Two layers:
 * 1) Brand tokens (this file) — company identity, shared by website + dashboard.
 * 2) Semantic tokens (`--background`, `--foreground`, … in index.css) — adapt to
 *    light/dark mode for readable app chrome *around* the brand.
 *
 * Navbar / Sidebar text on brand fills must use `onBrand` (typically white),
 * never `foreground` (body text on page surfaces).
 *
 * Do NOT add separate navbar/sidebar color keys — map chrome to `primary` +
 * `onBrand` so website and dashboard stay consistent as the brand evolves.
 */

// ─── Shape ─────────────────────────────────────────────────────────────────

export const THEME_KEYS = [
  "primary",
  "secondary",
  "softSurface",
  "pageBackground",
  "foreground",
  "accent",
  "onBrand",
] as const;

export type ThemeKey = (typeof THEME_KEYS)[number];
export type ThemePalette = Record<ThemeKey, string>;

/** Editor / docs metadata — usage copy lives in i18n (`theme.guide.<key>.*`). */
export const THEME_GUIDE_ORDER: ThemeKey[] = [...THEME_KEYS];

// ─── Defaults ───────────────────────────────────────────────────────────────

/**
 * Applied when no theme is found in the database.
 * Example (fashion / magenta) that admins often start from:
 *   primary #C026D3, secondary #211827, softSurface #FCE7F3,
 *   pageBackground #FFF8FC, foreground #211827, accent #DB2777, onBrand #FFFFFF
 */
export const DEFAULT_THEME: ThemePalette = {
  primary: "#6D28D9",
  secondary: "#3B0764",
  softSurface: "#F3F0F8",
  pageBackground: "#FAFAFA",
  foreground: "#18181B",
  accent: "#8B5CF6",
  onBrand: "#FFFFFF",
};

/**
 * Suggested starter palette matching the admin example (valid structure).
 * Kept for the theme editor “Apply example” action.
 */
export const EXAMPLE_FASHION_THEME: ThemePalette = {
  primary: "#C026D3",
  secondary: "#211827",
  softSurface: "#FCE7F3",
  pageBackground: "#FFF8FC",
  foreground: "#211827",
  accent: "#DB2777",
  onBrand: "#FFFFFF",
};

// ─── CSS variable map ────────────────────────────────────────────────────────

/** Maps every ThemePalette key to its CSS custom-property name. */
export const CSS_VAR_MAP: Record<keyof ThemePalette, string> = {
  primary: "--brand-primary",
  secondary: "--brand-secondary",
  softSurface: "--brand-soft-surface",
  pageBackground: "--brand-page-background",
  foreground: "--brand-foreground",
  accent: "--brand-accent",
  onBrand: "--brand-on-brand",
};

// ─── Helper ──────────────────────────────────────────────────────────────────

/**
 * Merges a partial / untrusted theme (e.g. from the database) with the
 * defaults, so every key is always present and valid.
 */
export function resolveTheme(
  partial?: Partial<ThemePalette> | Record<string, unknown> | null,
): ThemePalette {
  if (!partial || typeof partial !== "object") return { ...DEFAULT_THEME };

  const source = partial as Record<string, unknown>;
  const color = (key: string): string | undefined => {
    const value = source[key];
    return typeof value === "string" && /^#[0-9A-Fa-f]{6}$/.test(value)
      ? value.toUpperCase()
      : undefined;
  };

  return {
    primary: color("primary") ?? color("primaryColor") ?? DEFAULT_THEME.primary,
    secondary:
      color("secondary") ??
      color("sidebarBg") ??
      color("headerBg") ??
      DEFAULT_THEME.secondary,
    softSurface:
      color("softSurface") ?? color("contentBg") ?? DEFAULT_THEME.softSurface,
    pageBackground:
      color("pageBackground") ??
      color("contentBg") ??
      DEFAULT_THEME.pageBackground,
    foreground: color("foreground") ?? DEFAULT_THEME.foreground,
    accent: color("accent") ?? color("accentColor") ?? DEFAULT_THEME.accent,
    onBrand: color("onBrand") ?? color("textColor") ?? DEFAULT_THEME.onBrand,
  };
}
